import React, { useRef, useCallback, useState, useEffect } from 'react';
import { useToolStore } from '../../store/useToolStore';
import { useAuth } from '../../context/AuthContext';
import { Upload, X, Brush as BrushIcon, Hexagon as PolygonIcon, Lasso as LassoIcon, Sparkles as SparklesIcon, Undo as UndoIcon, Redo as RedoIcon, Layers as CompareIcon, Download as DownloadIcon, Upload as UploadIcon, Settings2 as AdjustmentsHorizontalIcon, Eye, Maximize } from 'lucide-react';
import FilmstripOverlay from './FilmstripOverlay';
import { ImageEditor } from '../ImageEditor';
import { ImageComparator } from '../ImageComparator';
import { Loader } from '../Loader';
import { callGeminiImageEditor } from '../../services/geminiService';
import { synthesizePrompt } from '../../services/promptSynthesizer';
import type { Point, GenerationMode } from '../../types';

const Viewport: React.FC = () => {
    const { profile, refreshProfile } = useAuth();

    // Global Store
    const compareMode = useToolStore((state) => state.compareMode);
    const setCompareMode = useToolStore((state) => state.setCompareMode);
    const uploadedImage = useToolStore((state) => state.uploadedImage);
    const setUploadedImage = useToolStore((state) => state.setUploadedImage);
    const prompt = useToolStore((state) => state.prompt);
    const generationTrigger = useToolStore((state) => state.generationTrigger);

    // Canvas State
    const history = useToolStore((state) => state.history);
    const historyIndex = useToolStore((state) => state.historyIndex);
    const toolMode = useToolStore((state) => state.toolMode);
    const setToolMode = useToolStore((state) => state.setToolMode);
    const brushSize = useToolStore((state) => state.brushSize);
    const setBrushSize = useToolStore((state) => state.setBrushSize);
    const brushColor = useToolStore((state) => state.brushColor);
    const setBrushColor = useToolStore((state) => state.setBrushColor);
    const selection = useToolStore((state) => state.selection);
    const setSelection = useToolStore((state) => state.setSelection);
    const pushToHistory = useToolStore((state) => state.pushToHistory);
    const undo = useToolStore((state) => state.undo);
    const redo = useToolStore((state) => state.redo);

    const activeImage = history.length > 0 ? history[historyIndex] : null;

    // Filmstrip State
    const [sessionImages, setSessionImages] = useState<string[]>([]);
    const [activeSessionIndex, setActiveSessionIndex] = useState<number>(-1);

    // Sync uploadedImage with session selection if session is active
    useEffect(() => {
        if (sessionImages.length > 0 && sessionImages[activeSessionIndex]) {
            setUploadedImage(sessionImages[activeSessionIndex]);
        }
    }, [activeSessionIndex, sessionImages, setUploadedImage]);



    // Local State for Generation
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [showOriginal, setShowOriginal] = useState<boolean>(false);

    // Settings State
    const [aiStrength, setAiStrength] = useState<number>(62);
    const [resemblance, setResemblance] = useState<number>(13);
    const [clarity, setClarity] = useState<number>(50);
    const [sharpness, setSharpness] = useState<number>(50);
    const [negativePrompt, setNegativePrompt] = useState<string>('');

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                if (event.target?.result) {
                    setUploadedImage(event.target.result as string);
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        const file = e.dataTransfer.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                if (event.target?.result) {
                    setUploadedImage(event.target.result as string);
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const handleLassoComplete = useCallback((points: Point[], dimensions: { width: number; height: number }) => {
        const newSelection = { points, dimensions };
        setSelection(newSelection);
    }, [setSelection]);

    const handleDrawingComplete = useCallback((newImageDataUrl: string) => {
        if (!newImageDataUrl) return;
        pushToHistory(newImageDataUrl);
    }, [pushToHistory]);

    // Land Typology State
    // Land Typology State
    const { landTypology, landSettings } = useToolStore();
    const activeTool = useToolStore((state) => state.activeTool); // Moved up for scope visibility

    // Generation Logic
    const handleGenerate = useCallback(async () => {
        if (!activeImage || !selection || selection.points.length < 3) {
            setError('Please select an area.');
            return;
        }

        if (profile?.credits <= 0) {
            setError("Insufficient credits.");
            return;
        }

        setIsLoading(true);
        setError(null);

        // Clear previous session images if starting a new batch
        // setSessionImages([]); // FIX: Do not clear, we want to append for Lens/Variants
        // setActiveSessionIndex(0);

        // Use the centralized Prompt Synthesizer to include all active tool params
        // const finalPrompt = synthesizePrompt(prompt); <-- REMOVED, we synthesize per-task now

        // Get variations count from store (default 1)
        const count = useToolStore.getState().variationsCount || 1;
        const newImages: string[] = [];

        // Determine Output Mode
        const outputMode = (activeTool === 'lens' || activeTool === 'magic-touch') ? 'standalone' : 'composite';

        // Initialize generation tasks
        // By default, we have one task with no overrides
        let generationTasks: Array<{ override?: Record<string, any>, suffix?: string }> = [{}];

        // SPECIAL LOGIC: Handle Multi-View Generation for Lens Tool
        if (activeTool === 'lens') {
            const viewpoints = useToolStore.getState().lensParams.camera.viewpoints;
            if (viewpoints && viewpoints.length > 0) {
                // If we have multiple viewpoints, we generate one distinct image set PER viewpoint
                generationTasks = viewpoints.map(vp => ({
                    override: { 'lensParams.camera.viewpoints': vp }, // Force single viewpoint
                    suffix: ` (${vp})` // Optional: store metadata if needed later
                }));
            }
        }

        try {
            // Updated Loop: Iterate through tasks (e.g. Viewpoints) AND variations count
            for (const task of generationTasks) {

                // Synthesize prompt specific to this task (e.g. specifically "Top Down")
                const taskPrompt = synthesizePrompt(prompt, task.override || {});
                const finalPromptForTask = taskPrompt;

                for (let i = 0; i < count; i++) {
                    const resultBase64 = await callGeminiImageEditor(
                        activeImage,
                        selection.points,
                        selection.dimensions,
                        finalPromptForTask,
                        null,
                        'modify',
                        negativePrompt,
                        aiStrength,
                        resemblance,
                        clarity,
                        sharpness,
                        profile?.id,
                        outputMode
                    );

                    // Progressive Update: Add image to session as soon as it arrives
                    const newImage = resultBase64;
                    newImages.push(newImage); // Keep track for final summary if needed

                    setSessionImages(prev => {
                        const updated = [...prev, newImage];
                        return updated.slice(-7); // Maintain limit
                    });

                    // If this is the VERY FIRST image of the batch, show it immediately on main canvas
                    // to give instant feedback.
                    if (newImages.length === 1 && !compareMode) {
                        pushToHistory(newImage);
                        setActiveSessionIndex(prev => prev + 1); // Select it
                    }
                }
            }

            // Sync profile credits
            await refreshProfile();

        } catch (err) {
            console.error(err);
            setError(err instanceof Error ? err.message : 'Generation failed.');
        } finally {
            setIsLoading(false);
        }
    }, [activeImage, selection, prompt, negativePrompt, aiStrength, resemblance, clarity, sharpness, profile, refreshProfile, pushToHistory, landTypology, landSettings, activeTool]);

    const lastGenTrigger = useRef(0);

    // Trigger generation when global trigger increments
    useEffect(() => {
        if (generationTrigger > lastGenTrigger.current && activeImage && selection && prompt) {
            lastGenTrigger.current = generationTrigger;
            handleGenerate();
        }
    }, [generationTrigger, activeImage, selection, prompt, handleGenerate]);

    const handleDownload = () => {
        if (!activeImage) return;
        const link = document.createElement('a');
        link.href = activeImage;
        link.download = `edit-${Date.now()}.png`;
        link.click();
    };

    const isUndoable = historyIndex > 0;
    const isRedoable = historyIndex < history.length - 1;
    const isReady = !!activeImage && !!selection && selection.points.length > 2;

    // activeTool already defined above

    if (activeTool === 'gallery') return null;

    return (
        <div className="flex-1 bg-black relative overflow-hidden flex flex-col">
            {/* Main Canvas Area */}
            <div
                className="flex-1 relative flex items-center justify-center bg-stone-900/50 p-8 overflow-hidden"
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
            >
                {activeImage ? (
                    <div className="relative w-full h-full flex items-center justify-center">
                        {/* Background Pattern */}
                        <div className="absolute inset-0 bg-[url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAAMUlEQVQ4T2NkYGAQYcAP3uCTZhw1gGGYhAGBZIA/nYDCgBDAm9BGDWAAJyRCgLaBCAAgXwixzAS0pgAAAABJRU5ErkJggg==')] bg-repeat opacity-20 pointer-events-none" />

                        {compareMode && historyIndex > 0 ? (
                            <div className="w-full h-full flex items-center justify-center z-10">
                                <ImageComparator
                                    beforeSrc={history[historyIndex - 1]}
                                    afterSrc={activeImage}
                                />
                            </div>
                        ) : (
                            <div className="w-full h-full flex items-center justify-center z-10">
                                <ImageEditor
                                    key={historyIndex}
                                    imageSrc={showOriginal && history.length > 0 ? history[0] : activeImage}
                                    onLassoComplete={handleLassoComplete}
                                    onDrawingComplete={handleDrawingComplete}
                                    selection={selection}
                                    toolMode={toolMode}
                                    brushColor={brushColor}
                                    brushSize={brushSize}
                                />
                            </div>
                        )}

                        {/* Clear Button */}
                        <div className="absolute top-4 right-4 z-20">
                            <button
                                onClick={() => setUploadedImage(null)}
                                className="bg-gray-900/80 backdrop-blur-sm p-2 rounded-full text-red-400 hover:text-red-300 hover:bg-gray-800 transition-colors border border-gray-700"
                                title="Clear Image"
                            >
                                <X size={16} />
                            </button>
                        </div>

                        {/* Quick Access Original View */}
                        <div className="absolute bottom-4 left-4 z-50">
                            <button
                                onMouseDown={() => setShowOriginal(true)}
                                onMouseUp={() => setShowOriginal(false)}
                                onMouseLeave={() => setShowOriginal(false)}
                                onTouchStart={() => setShowOriginal(true)}
                                onTouchEnd={() => setShowOriginal(false)}
                                className="bg-indigo-600/90 hover:bg-indigo-500 text-white p-3 rounded-full shadow-lg border border-indigo-400/50 backdrop-blur-sm transition-all transform active:scale-95"
                                title="Hold to view original"
                            >
                                <Eye size={20} />
                            </button>
                        </div>

                        {/* Loading State - Non-blocking UI */}
                        {isLoading && (
                            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 bg-indigo-900/90 text-white px-4 py-2 rounded-full text-sm flex items-center gap-2 border border-indigo-500 shadow-xl animate-pulse">
                                <SparklesIcon className="w-4 h-4 animate-spin" />
                                <span>Generating...</span>
                            </div>
                        )}

                        {/* Error Toast */}
                        {error && (
                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50 bg-red-900/90 text-white px-4 py-2 rounded-lg text-sm border border-red-700">
                                {error}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="text-center p-10 border-2 border-dashed border-gray-800 rounded-xl hover:border-gray-700 transition-colors bg-stone-950/50">
                        <Upload size={32} className="text-gray-500 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-300 mb-2">Upload Image</h3>
                        <p className="text-sm text-gray-500 mb-6">Drag & drop or click to browse</p>
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                        >
                            Select File
                        </button>
                        <input
                            type="file"
                            ref={fileInputRef}
                            className="hidden"
                            accept="image/*"
                            onChange={handleFileChange}
                        />
                    </div>
                )}

                {/* Filmstrip Overlay */}
                {sessionImages.length > 0 && (
                    <FilmstripOverlay
                        images={sessionImages}
                        selectedIndex={activeSessionIndex}
                        onSelect={setActiveSessionIndex}
                        onClear={() => {
                            setSessionImages([]);
                            setUploadedImage(null);
                        }}
                    />
                )}
            </div>

            {/* Bottom Toolbar - Only shown when activeImage is present */}
            {activeImage && (
                <div className="h-14 bg-black border-t border-zinc-800 flex flex-row items-center px-4 gap-4 z-30 shrink-0 w-full justify-between overflow-x-auto no-scrollbar">
                    <div className="flex flex-row gap-4 w-full items-center shrink-0">
                        {!compareMode && (
                            <div className="flex flex-row gap-2">
                                <button onClick={() => setToolMode('brush')} className={`p-2 rounded-lg transition-colors flex justify-center ${toolMode === 'brush' ? 'bg-indigo-600 text-white' : 'text-zinc-400 hover:text-white hover:bg-zinc-800'}`} title="Brush"><BrushIcon className="h-4 w-4" /></button>
                                <button onClick={() => setToolMode('freehand')} className={`p-2 rounded-lg transition-colors flex justify-center ${toolMode === 'freehand' ? 'bg-indigo-600 text-white' : 'text-zinc-400 hover:text-white hover:bg-zinc-800'}`} title="Lasso"><LassoIcon className="h-4 w-4" /></button>
                                <button onClick={() => setToolMode('polygonal')} className={`p-2 rounded-lg transition-colors flex justify-center ${toolMode === 'polygonal' ? 'bg-indigo-600 text-white' : 'text-zinc-400 hover:text-white hover:bg-zinc-800'}`} title="Polygon"><PolygonIcon className="h-4 w-4" /></button>
                                <button onClick={() => setToolMode('all')} className={`p-2 rounded-lg transition-colors flex justify-center ${toolMode === 'all' ? 'bg-indigo-600 text-white' : 'text-zinc-400 hover:text-white hover:bg-zinc-800'}`} title="Select All"><Maximize className="h-4 w-4" /></button>
                            </div>
                        )}

                        {!compareMode && toolMode === 'brush' && (
                            <>
                                <div className="h-6 w-px bg-zinc-800 shrink-0" />
                                <div className="flex flex-row items-center gap-4">
                                    <div className="relative w-6 h-6 rounded-full overflow-hidden border border-zinc-700 shrink-0">
                                        <input type="color" value={brushColor} onChange={(e) => setBrushColor(e.target.value)} className="absolute -top-1/2 -left-1/2 w-[200%] h-[200%] cursor-pointer p-0 m-0" />
                                    </div>
                                    <div className="w-32 flex items-center justify-center">
                                        <input
                                            type="range"
                                            min="1"
                                            max="100"
                                            value={brushSize}
                                            onChange={(e) => setBrushSize(Number(e.target.value))}
                                            className="w-full h-1 accent-indigo-600 bg-zinc-800 rounded-lg cursor-pointer"
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        {!compareMode && (
                            <>
                                <div className="h-6 w-px bg-zinc-800 shrink-0" />
                                <button
                                    onClick={handleGenerate}
                                    disabled={isLoading || !isReady}
                                    className="p-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-500 disabled:bg-zinc-800 disabled:text-zinc-600 transition-colors shadow-lg shadow-indigo-900/20 w-8 h-8 flex items-center justify-center shrink-0"
                                    title="Generate"
                                >
                                    <SparklesIcon className="w-4 h-4" />
                                </button>
                            </>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-row gap-2 items-center">
                        <button onClick={undo} disabled={!isUndoable} className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg disabled:opacity-30 transition-all flex justify-center" title="Undo"><UndoIcon className="h-4 w-4" /></button>
                        <button onClick={redo} disabled={!isRedoable} className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg disabled:opacity-30 transition-all flex justify-center" title="Redo"><RedoIcon className="h-4 w-4" /></button>
                        <div className="h-6 w-px bg-zinc-800 shrink-0 mx-1" />
                        <button onClick={() => setCompareMode(!compareMode)} disabled={!activeImage || history.length < 2} className={`p-2 rounded-lg disabled:opacity-30 transition-all flex justify-center ${compareMode ? 'bg-indigo-600 text-white' : 'text-zinc-400 hover:text-white hover:bg-zinc-800'}`} title="Compare"><CompareIcon className="h-4 w-4" /></button>
                        <button onClick={() => fileInputRef.current?.click()} className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-all flex justify-center" title="Upload New"><UploadIcon className="h-4 w-4" /></button>
                        <button onClick={handleDownload} className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-all flex justify-center" title="Download"><DownloadIcon className="h-4 w-4" /></button>
                    </div>
                </div>
            )}
        </div >
    );
};

export default Viewport;
